import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const MemberCards = ({ members, selectedMembers, onSelectMember, onViewMember }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-success/10', text: 'text-success', label: 'Active' },
      inactive: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Inactive' },
      frozen: { bg: 'bg-primary/10', text: 'text-primary', label: 'Frozen' },
      expired: { bg: 'bg-error/10', text: 'text-error', label: 'Expired' },
      trial: { bg: 'bg-warning/10', text: 'text-warning', label: 'Trial' }
    };

    const config = statusConfig?.[status] || statusConfig?.inactive;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const statusConfig = {
      paid: { icon: 'CheckCircle2', color: 'text-success', label: 'Paid' },
      pending: { icon: 'Clock', color: 'text-warning', label: 'Pending' },
      overdue: { icon: 'AlertCircle', color: 'text-error', label: 'Overdue' },
      failed: { icon: 'XCircle', color: 'text-error', label: 'Failed' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <div className={`flex items-center gap-1 ${config?.color}`}>
        <Icon name={config?.icon} size={14} />
        <span className="text-xs font-medium">{config?.label}</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {members?.map((member) => (
        <div
          key={member?.id}
          className="bg-card rounded-xl border border-border p-4 hover:shadow-md transition-all"
        >
          <div className="flex items-start gap-3 mb-4">
            <Checkbox
              checked={selectedMembers?.includes(member?.id)}
              onChange={(e) => onSelectMember(member?.id, e?.target?.checked)}
            />
            <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-muted">
              <Image
                src={member?.avatar}
                alt={member?.avatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-foreground truncate">{member?.name}</h3>
              <p className="text-sm text-muted-foreground truncate">{member?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(member?.status)}
                {getPaymentBadge(member?.paymentStatus)}
              </div>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Membership:</span>
              <span className="font-medium text-foreground">{member?.membershipType}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Expires:</span>
              <span className="font-medium text-foreground">{formatDate(member?.expiryDate)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Last Check-In:</span>
              <span className="font-medium text-foreground">{formatDate(member?.lastCheckIn)}</span>
            </div>
          </div>

          {member?.flags?.length > 0 && (
            <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border">
              <span className="text-xs text-muted-foreground">Flags:</span>
              {member?.flags?.map((flag, index) => (
                <div
                  key={index}
                  className="flex items-center gap-1 px-2 py-1 rounded-full bg-warning/10"
                  title={flag?.label}
                >
                  <Icon name={flag?.icon} size={12} color="var(--color-warning)" />
                  <span className="text-xs text-warning">{flag?.label}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              fullWidth
              onClick={() => onViewMember(member?.id)}
              iconName="Eye"
              iconPosition="left"
            >
              View Profile
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
            >
              <span className="sr-only">Edit</span>
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MemberCards;